import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGuard from "@/components/RoleGuard";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Resellers from "@/pages/Resellers";
import Users from "@/pages/Users";
import Pricing from "@/pages/Pricing";
import Wallets from "@/pages/Wallets";
import Campaigns from "@/pages/Campaigns";
import Reports from "@/pages/Reports";
import CDR from "@/pages/CDR";
import SystemSettings from "@/pages/SystemSettings";
import AdminBranding from "@/pages/AdminBranding";
import OpensipsServers from "@/pages/OpensipsServers";
import MediaServers from "@/pages/MediaServers";
import AIEngines from "@/pages/AIEngines";
import AICatalog from "@/pages/AICatalog";
import VicidialServers from "@/pages/VicidialServers";
import GlobalTemplates from "@/pages/GlobalTemplates";
import BillingPricing from "@/pages/BillingPricing";
import AdminPayments from "@/pages/AdminPayments";
import CallLimit from "@/pages/CallLimit";
import AccountBilling from "@/pages/AccountBilling";
import AccountPayments from "@/pages/AccountPayments";
import ResellerPricing from "@/pages/ResellerPricing";
import WhiteLabeling from "@/pages/WhiteLabeling";
import LiveCalls from "@/pages/LiveCalls";
import SystemLogs from "@/pages/SystemLogs";
import ImpersonateLanding from "@/pages/ImpersonateLanding";
import StatsReport from "@/pages/StatsReport";
import Commissions from "@/pages/Commissions";
import PaymentHistory from "@/pages/PaymentHistory";
import { ResellerBillingHistory, ResellerPaymentHistory } from "@/pages/ResellerHistory";
import CustomerSpeech from "@/pages/CustomerSpeech";
import KnowledgeBases from "@/pages/KnowledgeBases";
import StaffKnowledgeBases from "@/pages/StaffKnowledgeBases";
import StaffKnowledgeBaseView from "@/pages/StaffKnowledgeBaseView";
import Landing from "@/pages/Landing";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Profile from "@/pages/Profile";
import Features from "@/pages/Features";
import Benefits from "@/pages/Benefits";
import KnowledgeBase from "@/pages/KnowledgeBase";
import VoiceAgents from "@/pages/agents/VoiceAgents";
import AIAgents from "@/pages/agents/AIAgents";
import HybridAgents from "@/pages/agents/HybridAgents";
import { useAuth } from "@/context/AuthContext";
import { BrandProvider } from "@/context/BrandContext";
import SeoMeta from "@/components/SeoMeta";

function PaymentHistoryGate() {
  const { user } = useAuth();
  if (user?.role === "reseller") return <ResellerPaymentHistory />;
  return <PaymentHistory channel="direct" />;
}

function RootGate() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  return user ? <Navigate to="/dashboard" replace /> : <Landing />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <BrandProvider>
        <SeoMeta />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/impersonate" element={<ImpersonateLanding />} />
            <Route path="/agents/voice" element={<VoiceAgents />} />
            <Route path="/agents/ai" element={<AIAgents />} />
            <Route path="/agents/hybrid" element={<HybridAgents />} />
            <Route path="/features" element={<Features />} />
            <Route path="/benefits" element={<Benefits />} />
            <Route path="/knowledge-base" element={<KnowledgeBase />} />
            <Route path="/" element={<RootGate />} />
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route path="/profile" element={<Profile />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/customers" element={<RoleGuard allow={["admin", "reseller"]} menu="customers"><Customers /></RoleGuard>} />
              <Route path="/resellers" element={<RoleGuard allow={["admin"]} menu="resellers"><Resellers /></RoleGuard>} />
              <Route path="/users" element={<RoleGuard allow={["admin"]} menu="users"><Users /></RoleGuard>} />
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/live-calls" element={<RoleGuard allow={["admin", "reseller", "customer"]} menu="live-calls"><LiveCalls /></RoleGuard>} />
              <Route path="/text-to-speech" element={<RoleGuard allow={["customer"]}><CustomerSpeech /></RoleGuard>} />
              <Route path="/knowledge-bases" element={<RoleGuard allow={["customer"]}><KnowledgeBases /></RoleGuard>} />
              <Route path="/staff-knowledge-bases" element={<RoleGuard allow={["admin", "staff"]} menu="staff-knowledge-bases"><StaffKnowledgeBases /></RoleGuard>} />
              <Route path="/staff-knowledge-bases/:kbId" element={<RoleGuard allow={["admin", "staff"]} menu="staff-knowledge-bases"><StaffKnowledgeBaseView /></RoleGuard>} />
              <Route path="/templates" element={<RoleGuard allow={["admin"]} menu="templates"><GlobalTemplates /></RoleGuard>} />
              <Route path="/servers/opensips" element={<RoleGuard allow={["admin"]} menu="servers-opensips"><OpensipsServers /></RoleGuard>} />
              <Route path="/servers/media" element={<RoleGuard allow={["admin"]} menu="servers-media"><MediaServers /></RoleGuard>} />
              <Route path="/servers/ai-engines" element={<RoleGuard allow={["admin"]} menu="servers-ai-engines"><AIEngines /></RoleGuard>} />
              <Route path="/ai-bots/models" element={<RoleGuard allow={["admin"]} menu="ai-bots-models"><AICatalog kind="models" /></RoleGuard>} />
              <Route path="/ai-bots/tts" element={<RoleGuard allow={["admin"]} menu="ai-bots-tts"><AICatalog kind="tts" /></RoleGuard>} />
              <Route path="/ai-bots/stt" element={<RoleGuard allow={["admin"]} menu="ai-bots-stt"><AICatalog kind="stt" /></RoleGuard>} />
              <Route path="/servers/vicidial" element={<RoleGuard allow={["admin", "customer"]} menu="servers-vicidial"><VicidialServers /></RoleGuard>} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/billing/pricing" element={<RoleGuard allow={["admin"]} menu="billing-pricing"><BillingPricing /></RoleGuard>} />
              <Route path="/billing/payments" element={<RoleGuard allow={["admin"]} menu="billing-payments"><AdminPayments /></RoleGuard>} />
              <Route path="/call-limit" element={<RoleGuard allow={["reseller", "customer"]}><CallLimit /></RoleGuard>} />
              <Route path="/billing" element={<RoleGuard allow={["reseller", "customer"]}><AccountBilling /></RoleGuard>} />
              <Route path="/payments" element={<RoleGuard allow={["reseller", "customer"]}><AccountPayments /></RoleGuard>} />
              <Route path="/reseller-pricing" element={<RoleGuard allow={["reseller"]}><ResellerPricing /></RoleGuard>} />
              <Route path="/white-labeling" element={<RoleGuard allow={["reseller"]}><WhiteLabeling /></RoleGuard>} />
              <Route path="/wallet" element={<RoleGuard allow={["reseller", "customer"]}><Wallets /></RoleGuard>} />
              <Route path="/reports" element={<RoleGuard allow={["admin", "reseller", "customer"]} menu="reports"><Reports /></RoleGuard>} />
              <Route path="/reports/stats" element={<RoleGuard allow={["admin"]} menu="reports-stats"><StatsReport /></RoleGuard>} />
              <Route path="/reports/payment-history" element={<RoleGuard allow={["admin", "reseller"]} menu="reports-payment-history"><PaymentHistoryGate /></RoleGuard>} />
              <Route path="/reports/reseller-payment" element={<RoleGuard allow={["admin"]} menu="reports-reseller-payment"><PaymentHistory channel="reseller" /></RoleGuard>} />
              <Route path="/reports/commissions" element={<RoleGuard allow={["admin", "staff"]} menu="reports-commissions"><Commissions /></RoleGuard>} />
              <Route path="/reports/billing-history" element={<RoleGuard allow={["reseller"]}><ResellerBillingHistory /></RoleGuard>} />
              <Route path="/cdr" element={<RoleGuard allow={["admin", "reseller", "customer"]} menu="cdr"><CDR /></RoleGuard>} />
              <Route path="/cdr/reseller" element={<RoleGuard allow={["admin"]} menu="cdr-reseller"><CDR channel="reseller" /></RoleGuard>} />
              <Route path="/settings" element={<RoleGuard allow={["admin"]} menu="settings"><SystemSettings /></RoleGuard>} />
              <Route path="/branding" element={<RoleGuard allow={["admin"]} menu="branding"><AdminBranding /></RoleGuard>} />
              <Route path="/system-logs" element={<RoleGuard allow={["admin"]} menu="system-logs"><SystemLogs /></RoleGuard>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </BrandProvider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
